import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { matchSorter } from 'match-sorter';

interface ControlledAutocompleteMultiselectProps<TFields extends FieldValues, Option extends { value: string; id: number | string }> {
  /** The options to be displayed in the autocomplete, they have to mandatory have a value and an id */
  options: Option[];
  /** This is a prefilled value from control */
  defaultValue?: Option[];
  /** The label to be displayed in the autocomplete */
  label: string;
  /** The placeholder to be displayed in the autocomplete */
  placeholder?: string;
  /** The maximum number of tags that can be displayed in the autocomplete, default is 2 */
  limitTags?: number;
  /** The key of the option that will be displayed in the autocomplete, default is 'value', recommended to use the same prop that matches your renderOption */
  valueInOption: keyof Option;
  /** Optional, if you want to customize the way the options are displayed, you can pass a function that will receive the props and the option */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: Option) => React.ReactNode;
  /** The name of the field in the form, this needs to match the name of the field in the form */
  name: Path<TFields>;
  /** The form control from react-hook-form */
  control: Control<TFields>;
  /** Optional, if you want to validate the new value, you can pass a function that will receive the new value and return a boolean */
  addedNewOptionOnEnterValidation?: (value: string) => boolean;
  /** Optional, if you want to customize the no options text, you can pass a string */
  noOptionsText?: string;
  /** Optional, if you want to disable the autocomplete, you can pass a boolean */
  disabled?: boolean;
  /** Optional, callback fired when the user types in the input — use this to drive server-side search */
  onInputChange?: (value: string) => void;
}

export const ControlledAutocompleteMultiselect = <TFields extends FieldValues, Option extends { value: string; id: number | string }>({
  options,
  defaultValue,
  label,
  placeholder,
  limitTags,
  valueInOption = 'value',
  renderOption,
  addedNewOptionOnEnterValidation,
  name,
  control,
  noOptionsText,
  disabled,
  onInputChange,
}: ControlledAutocompleteMultiselectProps<TFields, Option>) => {
  const [internalOptionValues, setInternalOptionValues] = useState<Option[]>(options);
  const [internalInputValue, setInternalInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (options.length > 0) {
      setInternalOptionValues(options);
    }
  }, [options]);

  const {
    field: { value, onChange: onChangeField },
  } = useController({
    name,
    control,
  });

  const handleAutocompleteOnChange: (
    event: React.SyntheticEvent,
    value: Array<Option>,
    reason: 'createOption' | 'selectOption' | 'removeOption' | 'blur' | 'clear',
    details?: string
  ) => void = (event, value) => {
    onChangeField(value);
  };

  const renderOptionFromProps = (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: Option) => {
    if (!renderOption) return undefined;
    return renderOption(props, option);
  };

  const handleAddNewOptionOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (addedNewOptionOnEnterValidation && !addedNewOptionOnEnterValidation(internalInputValue)) {
        control.setError(name, { message: 'Invalid field' });
        return;
      }

      const newOption = { [valueInOption]: internalInputValue } as Option;
      setInternalOptionValues([...internalOptionValues, newOption]);
      onChangeField([...(value || []), newOption]);
      setInternalInputValue('');

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const filterOptions = (options: Option[], { inputValue }: { inputValue: string }) =>
    matchSorter(options, inputValue, { keys: [valueInOption as string] });

  return (
    <Autocomplete
      multiple
      limitTags={limitTags}
      {...(renderOption && { renderOption: renderOptionFromProps })}
      disabled={disabled}
      id="multiple-limit-tags"
      options={internalOptionValues}
      filterOptions={filterOptions}
      filterSelectedOptions
      value={value ?? []}
      getOptionLabel={(option) => option[valueInOption as string]}
      noOptionsText={noOptionsText ?? 'No option found'}
      isOptionEqualToValue={(option, value) => {
        if (option.id !== value.id) return false;
        return option[valueInOption] === value[valueInOption];
      }}
      defaultValue={defaultValue}
      onChange={(event, value, reason) => handleAutocompleteOnChange(event, value, reason)}
      onKeyDown={handleAddNewOptionOnEnter}
      renderTags={(value, getTagProps) => {
        return (value ?? [])?.map((option, index) => (
          <Chip
            avatar={<Avatar src={(option as any)?.avatar} />}
            {...getTagProps({ index })}
            key={index}
            label={option[valueInOption as string]}
          />
        ));
      }}
      renderInput={(params) => {
        return (
          <TextField
            label={label}
            placeholder={placeholder}
            onChange={(event) => {
              setInternalInputValue(event.target.value);
              onInputChange?.(event.target.value);
            }}
            type="text"
            variant="outlined"
            inputRef={inputRef}
            {...params}
            sx={{
              '& .MuiFormHelperText-root ': {
                marginLeft: '0px !important',
              },
            }}
            fullWidth
          />
        );
      }}
    />
  );
};
